/**
 * @module common/components/photo_capture
 */

import React, { Component, PropTypes } from 'react';
import { isMobile } from 'common/utils';
import Cropper from 'react-cropper';
import Webcam from 'react-webcam';


/**
 * Photo capture component.
 * On desktop, capture photos using user's webcam stream.
 * On mobile devices, delegate capturing to the browser by using a file input instead.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
class PhotoCapture extends Component {
  constructor(props) {
    super(props)
    this.state = {
      stage: 'done', finalPhoto: props.initialValue || null, tempPhoto: null, error: ''
    }
    this.snap = this.snap.bind(this)
    this.capture = this.capture.bind(this)
    this.save = this.save.bind(this)
    this.cancelCapture = this.cancelCapture.bind(this)
    this.cancelCropping = this.cancelCropping.bind(this)
    this.handleLeftAction = this.handleLeftAction.bind(this)
    this.handleRightAction = this.handleRightAction.bind(this)
    this.handleFileChange = this.handleFileChange.bind(this)
    this.onStartWebcamCapture = this.onStartWebcamCapture.bind(this)
    this.onStopWebcamCapture = this.onStopWebcamCapture.bind(this)
  }

  onStartWebcamCapture() {
    /* store webcam capturing state in `window` object so that it can be used accross the app */
    if (window.capturingWebcam) {
      this.setState({error: 'Webcam is already in use'})
    } else {
      window.capturingWebcam = true
      this.setState({stage: 'webcamCapturing', error: ''})
    }
  }

  onStopWebcamCapture(nextState) {
    /* store webcam capturing state in `window` object so that it can be used accross the app */
    window.capturingWebcam = false
    this.setState({...nextState})
  }

  snap() {
    const dataUri = this.refs.webcam.getScreenshot()
    this.onStopWebcamCapture({stage: 'cropping', tempPhoto: dataUri})
  }

  save() {
    const canvas = this.refs.cropper.getCroppedCanvas()

    if (!canvas) {
      return
    }

    const finalPhoto = canvas.toDataURL('image/jpeg')
    this.setState({stage: 'done', finalPhoto: finalPhoto})
    this.props.onChange(finalPhoto)
  }

  capture() {
    /* for mobile devices, the capture is handled automatically by the browser once the
       file input is activated. Therefore, the `webcamCapturing` stage is not used. */
    if (isMobile()) {
      this.refs.file_input.click()
      return
    }

    this.onStartWebcamCapture()
  }

  cancelCapture() {
    this.onStopWebcamCapture({stage: 'done'})
  }

  cancelCropping() {
    /* for mobile devices, the capture is handled automatically by the browser once the
       file input is activated. Therefore, the `webcamCapturing` stage is not used. */
    if (isMobile()) {
      this.setState({stage: 'done'})
    } else {
      this.onStartWebcamCapture()
    }
  }

  handleLeftAction() {
    const { stage } = this.state
    return {
      done: this.capture,
      webcamCapturing: this.cancelCapture,
      cropping: this.cancelCropping
    }[stage]()
  }

  handleRightAction() {
    const { stage } = this.state
    return {
      webcamCapturing: this.snap,
      cropping: this.save
    }[stage]()
  }

  handleFileChange(e) {
    /* read file as data URI */
    var files = e.target.files

    if (files.length < 1) return

    var filename = files[0].name
    var reader = new FileReader()

    reader.onload = (event) => {
      this.setState({stage: 'cropping', tempPhoto: event.target.result})
    }

    reader.onerror = (event) => {
      console.error("File could not be read! Code " + event.target.error.code)
    }

    reader.readAsDataURL(files[0])
  }

  renderStage() {
    /* render content according to stage in the workflow */
    const { stage, finalPhoto, tempPhoto } = this.state
    const { placeholder="https://placehold.it/334X222", cropperRatio=3/2 } = this.props

    return {
      /* photo taken and cropped */
      done: (
        <div key="done" className="capture-stage">
          {isMobile() &&
          <input type="file" ref="file_input" accept="image/*" onChange={this.handleFileChange} capture/>}
          <img src={finalPhoto || placeholder} />
        </div>
      ),

      /* capturing user's camera stream */
      webcamCapturing: <Webcam audio={false} ref="webcam" screenshotFormat="image/jpeg"
                          width={290} height={218}/>,

      /* cropping */
      cropping: <Cropper ref="cropper" key="cropper" src={tempPhoto} aspectRatio={cropperRatio}
                         minCropBoxWidth={250} viewMode={1}/>

    }[stage]
  }

  render() {
    const { stage, finalPhoto, error } = this.state
    const { photoLabel="Photo", readonly } = this.props
    /* left and right button labels according to stage */
    const btnLabels = {
      done: {
        left: readonly? null : (finalPhoto? "Replace ":"Add ") + photoLabel,
        right: null
      },
      webcamCapturing: {
        left: 'Cancel',
        right: 'Take'
      },
      cropping: {
        /* on mobile there's no `webcamCapturing` stage, so user would get back to the beginning */
        left: isMobile()? 'Cancel':'Retake',
        right: 'Done'
      }
    }[stage]

    return (
      <div className="photo-capture">
        { this.renderStage() }

        {error &&
        <div className="error">
          <i className="fa fa-exclamation-circle"></i> {error}
        </div>}

        <div className="photo-capture-controls">
          { !btnLabels.left? null :
          <button className="btn btn-md btn-secondary" onClick={this.handleLeftAction}>
            { btnLabels.left }
          </button> }

          { !btnLabels.right? null :
          <button className="btn btn-md btn-tertiary" onClick={this.handleRightAction}>
            { btnLabels.right }
          </button> }
        </div>

      </div>)
  }
}

PhotoCapture.propTypes = {
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  initialValue: PropTypes.string,
  photoLabel: PropTypes.string,
  cropperRatio: PropTypes.number,
  readonly: PropTypes.bool,
};

export default PhotoCapture;



// WEBPACK FOOTER //
// ./common/components/photo_capture.jsx